import { useEffect, useRef } from 'react';
import { deepEqual } from '../utils/deep-equal.js';
import {
  DependencyList,
  EffectCallback,
  UseEffectReturn,
} from './type.effect.js';

// https://github.com/kentcdodds/use-deep-compare-effect

function checkDeps<T extends DependencyList = DependencyList>(deps: T) {
  if (!deps || !deps.length) {
    throw new Error(
      'useDeepCompareEffect should not be used with no dependencies. Use React.useEffect instead.'
    );
  }
  if (deps.every(isPrimitive)) {
    throw new Error(
      'useDeepCompareEffect should not be used with dependencies that are all primitive values. Use React.useEffect instead.'
    );
  }
}

function isPrimitive(val: unknown) {
  return val === null || /^[usbn]/.test(typeof val);
}

function useDeepCompareMemoize<T>(value: T, myEqual = deepEqual) {
  const ref = useRef<T>();
  const signalRef = useRef<number>(0);

  if (!myEqual(value, ref.current)) {
    ref.current = value;
    signalRef.current += 1;
  }

  return [signalRef.current];
}

export function useDeepCompareEffect(
  callback: EffectCallback,
  dependencies: DependencyList,
  updatePhase = false
): UseEffectReturn {
  if (process.env.NODE_ENV !== 'production') {
    checkDeps(dependencies);
  }
  const mounted = useRef(false);

  return useEffect(() => {
    // Check if effect hook that invokes only on update.
    if (updatePhase) {
      if (mounted.current) {
        return callback();
      }
      mounted.current = true;
    } else {
      return callback();
    }
  }, useDeepCompareMemoize(dependencies));
}

export function useDeepCompareEffectNoCheck(
  callback: EffectCallback,
  dependencies: DependencyList,
  updatePhase = false
): UseEffectReturn {
  const mounted = useRef(false);

  return useEffect(() => {
    // Check if effect hook that invokes only on update.
    if (updatePhase) {
      if (mounted.current) {
        return callback();
      }
      mounted.current = true;
    } else {
      return callback();
    }
  }, useDeepCompareMemoize(dependencies));
}

export function useCustomCompareEffect<
  T extends DependencyList = DependencyList,
>(
  callback: EffectCallback,
  dependencies: T,
  equal: (newDep: T, oldDep: T) => boolean,
  updatePhase = false
) {
  const mounted = useRef(false);

  return useEffect(
    () => {
      // Check if effect hook that invokes only on update.
      if (updatePhase) {
        if (mounted.current) {
          return callback();
        }
        mounted.current = true;
      } else {
        return callback();
      }
    },
    useDeepCompareMemoize<T>(dependencies, equal)
  );
}
